import React from 'react'
// libs
import { isMobile } from 'react-device-detect'
import { useRouter } from 'hooks/useRouter'
import { clearFields } from 'redux-form'
import { useDispatch } from 'react-redux'
import { createPortal } from 'react-dom'
import { DragDropContext, Draggable, DraggableProvidedDragHandleProps, Droppable } from 'react-beautiful-dnd'
// components
import TableField from './components/TableField'
import TableHeadLabel from './components/TableHead/TableHeadLabel'
import AddFieldButton from '../AddFieldButton'
import ControlButtons from '../ControlButtons'
// hooks
import { useShowModal } from 'hooks/useShowModal'
import { useMobileScroll } from 'hooks/useMobileScroll'
import { useFloatingScrollbar } from 'hooks/useFloatingScrollbar'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// constants
import { Forms, Modals } from 'constants/index'
// types
import { IField, IFieldLinkInput, IFieldUpdatePositionInput } from 'types/graphql'
// views
import { TableIcon } from 'themes/views/objects'
import { DraggableContainer, DragHandle, DroppableContainer, TableContainer, TableContent, FloatingScrollbar } from './views'

export interface TableProps {
  field: IField
  fields: IField[]
  dragHandleProps?: DraggableProvidedDragHandleProps
}

const Table: React.FC<TableProps> = ({ field, fields, dragHandleProps }) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const scrollRef = useMobileScroll()
  const tableContentRef = React.useRef<HTMLDivElement>(null)

  const { floatBarRef, visible, left, width, scrollWidth } = useFloatingScrollbar(tableContentRef)

  const setTableContentRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      tableContentRef.current = node

      if (scrollRef && typeof scrollRef === 'object') {
        scrollRef.current = node
      }
    },
    [scrollRef]
  )

  const { riskTemplate, isRiskTemplate, isRisksRegister, deleteField, reorderFields } =
    React.useContext(RiskTemplateAPIContext)

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_RISK_MODAL })

  const isRegisters = isRisksRegister || !!router.query.multiple

  // Compute sticky left offset for columns that render <Column> (isMainForm = true)
  const stickyLeftByFieldId = React.useMemo(() => {
    const map = new Map<string, number>()

    if (isRegisters || isMobile) {
      return map
    }

    let offset = 0

    for (const item of fields) {
      const fieldLink = riskTemplate.fieldLinks.find(
        ({ field: { id }, parentFieldId }) => id === item.id && parentFieldId === field.id
      )

      const isMainForm = fieldLink?.fieldGroups.some(({ groupedByEntityId }) => groupedByEntityId === riskTemplate.id)

      if (isMainForm) {
        map.set(item.id, offset)
        offset += 200
      }
    }
    return map
  }, [fields, riskTemplate.fieldLinks, riskTemplate.id, field.id, isRegisters, isMobile])

  const deleteTable = async () => {
    await deleteField(field)

    dispatch(clearFields(Forms.RISK_TEMPLATE_FORM, false, false, ...fields.map(({ id }) => id)))
  }

  const onDragEnd = ({ source, destination }) => {
    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId) {
      const reorder = (list: IField[], startIdx: number, endIdx: number) => {
        const fieldsOrderInput: IFieldUpdatePositionInput[] = []
        const fieldLinksOrderInput: IFieldLinkInput[] = []

        const [removed] = list.splice(startIdx, 1)

        list.splice(endIdx, 0, removed)

        list.forEach((item, idx) => {
          if (item.position === idx) {
            return
          }

          const fieldLink = riskTemplate.fieldLinks.find(
            ({ field: { id }, parentFieldId }) => id === item.id && parentFieldId === field.id
          )

          if (fieldLink) {
            fieldLinksOrderInput.push({
              id: fieldLink.id,
              fieldId: item.id,
              position: idx,
              targetEntityId: fieldLink.targetEntityId
            })
          } else {
            fieldsOrderInput.push({
              id: item.id,
              position: idx
            })
          }
        })

        return {
          fieldsOrderInput,
          fieldLinksOrderInput
        }
      }

      const { fieldsOrderInput, fieldLinksOrderInput } = reorder(fields, source.index, destination.index)

      reorderFields(fieldsOrderInput, fieldLinksOrderInput)
    }
  }

  const onTableDelete = () => {
    showModal({
      width: 'sm',
      entity: 'table',
      handleConfirm: deleteTable
    })
  }

  return (
    <>
      {visible &&
        createPortal(
          <FloatingScrollbar ref={floatBarRef} $left={left} $width={width}>
            <div style={{ width: scrollWidth, height: 1 }} />
          </FloatingScrollbar>,
          document.body
        )}
      <DragDropContext onDragEnd={onDragEnd}>
        <TableContainer data-testid='risk-template-table' data-entity-id={field.id}>
          {isRiskTemplate && <ControlButtons field={field} onDeleteClick={onTableDelete} />}
          {isRiskTemplate && (
            <DragHandle {...dragHandleProps}>
              <TableIcon />
            </DragHandle>
          )}
          <TableContent ref={setTableContentRef}>
            <Droppable droppableId={`RISK_TABLE_${field.id}_FIELDS`} direction='horizontal'>
              {(provided) => (
                <DroppableContainer ref={provided.innerRef} {...provided.droppableProps}>
                  {fields.map((item, idx) => (
                    <Draggable index={idx} key={item.id} draggableId={item.id} isDragDisabled={!isRiskTemplate}>
                      {(provided) => (
                        <DraggableContainer
                          $stickyLeft={stickyLeftByFieldId.get(item.id)}
                          className={`table-column-${item.id}-${item.parentFieldId}`}
                          data-testid='risk-template-table-column'
                          data-entity-id={item.id}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={provided.draggableProps.style as React.CSSProperties}>
                          {!isRegisters && (
                            <TableHeadLabel
                              field={item}
                              label={item.label}
                              sticky={!isRegisters}
                              dragHandleProps={provided.dragHandleProps}
                            />
                          )}
                          <TableField field={item} />
                        </DraggableContainer>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </DroppableContainer>
              )}
            </Droppable>

            {isRiskTemplate && !router.query.archived && (
              <AddFieldButton parentField={field} label={isMobile ? ' ' : 'Add column'} />
            )}
          </TableContent>
        </TableContainer>
      </DragDropContext>
    </>
  )
}

export default Table
